import Quill, { Delta } from 'quill/core';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Emoji from '../../post/comment/Emoji';
import {
  applySkinToneToEmoji,
  DEFAULT_EMOJI_NAMES,
  getEmojiItemByName,
  searchEmoji,
} from '../../utils/emoji';
import { usePrefixListener } from '../PrefixListener';
import styles from './EmojiAutocompleter.module.scss';
import PrefixTriggeredMenu from './PrefixTriggeredMenu';

const MAX_RESULTS = 6;

const defaultEmojis = DEFAULT_EMOJI_NAMES.map(getEmojiItemByName);
const USE_PREFIX_LISTENER_OPTIONS = [{ prefix: ':', isAllowedInTitle: true }];

interface EmojiAutocompleterProps {
  quill: Quill;
  readOnly: boolean;
}

const EmojiAutocompleter = ({ quill, readOnly }: EmojiAutocompleterProps) => {
  const modifier = useAppSelector((state) => state.ui.emojiSkinToneModifier);

  const { bounds, text, prefix, reset } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );
  const query = text ? text.toLowerCase() : '';
  const isShowingMenu = !!bounds && !readOnly;

  return isShowingMenu ? (
    <PrefixTriggeredMenu
      quill={quill}
      onSelect={({ data: { tonedChar } }) => {
        const range = quill.getSelection(true);
        if (range == null || text == null || prefix == null) return;
        const deletionLength = text.length + prefix.length;
        const index = range.index - deletionLength;
        quill.updateContents(
          new Delta().retain(index).delete(deletionLength).insert(tonedChar),
          Quill.sources.USER,
        );
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        quill.setSelection(index + tonedChar.length, Quill.sources.USER);
      }}
      sections={[
        {
          items: (query ? searchEmoji(query, MAX_RESULTS) : defaultEmojis).map(
            (emoji) => ({
              id: emoji.name,
              data: { emoji, tonedChar: applySkinToneToEmoji(emoji, modifier) },
            }),
          ),
        },
      ]}
      query={query}
      onClose={() => reset?.()}
      bounds={bounds}
      renderItem={({ emoji, tonedChar }) => (
        <div className={styles.item}>
          <Emoji char={tonedChar} size="large" />:{emoji.name}
        </div>
      )}
      containerSize="small"
      testId="emojiAutocompleter"
    />
  ) : null;
};

export default EmojiAutocompleter;
